import React, { useId } from 'react';
import styled from 'styled-components';

const StyledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

const StyledLegend = styled.legend`
  padding: 0;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: var(--fs-b3);
  color: var(--c-fg-0);
`;

const StyledOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 12px;
  margin-bottom: 4px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.checked ? 'var(--c-fg-0)' : 'var(--c-border)')};
  background: ${(p) => (p.checked ? 'var(--c-border-light)' : 'transparent')};
  transition: 0.15s;

  &:hover {
    border-color: var(--c-fg-1);
  }

  .dot {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border-radius: 99px;
    box-sizing: border-box;
    border: ${(p) =>
      p.checked ? '6px solid var(--c-fg-0)' : '1px solid var(--c-input-border-unfocused)'};
    transition: 0.15s;
  }

  .title {
    display: block;
    font-weight: 500;
    font-size: var(--fs-b3);
    line-height: 1.3;
    color: var(--c-fg-0);
  }

  .description {
    display: block;
    margin-top: 4px;
    font-weight: 400;
    font-size: var(--fs-fp);
    line-height: 1.4;
    color: var(--c-fg-1);
  }
`;

const StyledInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  &:focus + .dot {
    outline: 1px solid var(--c-fg-0);
    outline-offset: 2px;
  }
`;

export interface RadioOption<T extends string> {
  value: T;
  title: React.ReactNode;
  description?: React.ReactNode;
}

export interface RadioGroupProps<T extends string> {
  /** Visible group label, rendered as the fieldset's legend. */
  label: React.ReactNode;
  options: ReadonlyArray<RadioOption<T>>;
  value?: T;
  onChange: (value: T) => void;
  className?: string;
}

export function RadioGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: RadioGroupProps<T>) {
  // scopes `name` to this instance, so several groups can sit on one page
  const name = useId();

  return (
    <StyledFieldset className={className}>
      <StyledLegend>{label}</StyledLegend>
      {options.map((option) => (
        <StyledOption
          key={option.value}
          checked={value === option.value}
          htmlFor={`${name}_${option.value}`}
        >
          <StyledInput
            type="radio"
            id={`${name}_${option.value}`}
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="dot" aria-hidden="true" />
          <span>
            <span className="title">{option.title}</span>
            {!!option.description && <span className="description">{option.description}</span>}
          </span>
        </StyledOption>
      ))}
    </StyledFieldset>
  );
}
